import { Heading, Stack, useDisclosure } from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { LinkButton } from '@blockpulse3/ui/commons';
import {
  CompanyAccess,
  CompanyOwners,
  CompanyRepresentatives,
  ExistingCompanyParameters,
  StepFormContextProvider,
} from '@blockpulse3/web-client/onboardings';

import { SettingsCompanyAccess } from './SettingsCompanyAccess';
import { SettingsCompanyEditModal } from './SettingsCompanyEditModal';
import { SettingsCompanyInformations } from './SettingsCompanyInformations';
import { SettingsCompanyOwners } from './SettingsCompanyOwners';
import { SettingsCompanyRepresentatives } from './SettingsCompanyRepresentatives';

type Props = {
  backHref?: string;
};

export function WorkspaceSettingsView({ backHref }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nWorkspaceSettings = useTranslations('WorkspaceSettings');

  const { isOpen: isOpenInfos, onOpen: onOpenInfos, onClose: onCloseInfos } = useDisclosure();
  const { isOpen: isOpenReps, onOpen: onOpenReps, onClose: onCloseReps } = useDisclosure();
  const { isOpen: isOpenOwners, onOpen: onOpenOwners, onClose: onCloseOwners } = useDisclosure();
  const { isOpen: isOpenAccess, onOpen: onOpenAccess, onClose: onCloseAccess } = useDisclosure();

  return (
    <>
      <Stack spacing="6">
        {backHref && <LinkButton label={i18nGeneral('Back')} route={backHref} />}
        <Heading>{i18nWorkspaceSettings('Title')}</Heading>
        <Stack spacing="6">
          <SettingsCompanyInformations onOpen={onOpenInfos} />
          <SettingsCompanyRepresentatives onOpen={onOpenReps} />
          <SettingsCompanyOwners onOpen={onOpenOwners} />
          <SettingsCompanyAccess onOpen={onOpenAccess} />
        </Stack>
      </Stack>
      {isOpenInfos && (
        <StepFormContextProvider>
          <SettingsCompanyEditModal
            isOpen={isOpenInfos}
            title={i18nWorkspaceSettings('CompanyInformationsTitle')}
            onClose={onCloseInfos}
          >
            <ExistingCompanyParameters
              isEditing={true}
              onCancel={onCloseInfos}
              onSubmit={onCloseInfos}
            />
          </SettingsCompanyEditModal>
        </StepFormContextProvider>
      )}
      {isOpenReps && (
        <StepFormContextProvider>
          <SettingsCompanyEditModal
            isOpen={isOpenReps}
            title={i18nWorkspaceSettings('RepresentativesTitle')}
            onClose={onCloseReps}
          >
            <CompanyRepresentatives
              isEditing={true}
              onCancel={onCloseReps}
              onSubmit={onCloseReps}
            />
          </SettingsCompanyEditModal>
        </StepFormContextProvider>
      )}
      {isOpenOwners && (
        <StepFormContextProvider>
          <SettingsCompanyEditModal
            isOpen={isOpenOwners}
            title={i18nWorkspaceSettings('OwnersTitle')}
            onClose={onCloseOwners}
          >
            <CompanyOwners isEditing={true} onCancel={onCloseOwners} onSubmit={onCloseOwners} />
          </SettingsCompanyEditModal>
        </StepFormContextProvider>
      )}
      {isOpenAccess && (
        <StepFormContextProvider>
          <SettingsCompanyEditModal
            isOpen={isOpenAccess}
            title={i18nWorkspaceSettings('OtherRolesTitle')}
            onClose={onCloseAccess}
          >
            <CompanyAccess isEditing={true} onCancel={onCloseAccess} onSubmit={onCloseAccess} />
          </SettingsCompanyEditModal>
        </StepFormContextProvider>
      )}
    </>
  );
}

export type WorkspaceSettingsViewProps = Props;
